import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Group {
  id: string;
  name: string;
  description: string;
  type: string;
  contactCount: number;
  settings: {
    allowDuplicates: boolean;
    autoValidate: boolean;
    requireEmail: boolean;
    requirePhone: boolean;
  };
}

interface GroupContextType {
  groups: Group[];
  addGroup: (group: Omit<Group, 'id' | 'contactCount'>) => void;
  updateGroup: (id: string, group: Partial<Group>) => void;
  deleteGroup: (id: string) => void;
}

const GroupContext = createContext<GroupContextType | undefined>(undefined);

export const useGroups = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error('useGroups must be used within a GroupProvider');
  }
  return context;
};

interface GroupProviderProps {
  children: ReactNode;
}

export const GroupProvider: React.FC<GroupProviderProps> = ({ children }) => {
  const [groups, setGroups] = useState<Group[]>([
    {
      id: '1',
      name: 'VIP Customers',
      description: 'High-value customers with special privileges',
      type: 'vip',
      contactCount: 1234,
      settings: {
        allowDuplicates: false,
        autoValidate: true,
        requireEmail: true,
        requirePhone: true
      }
    },
    {
      id: '2',
      name: 'Newsletter Subscribers',
      description: 'Customers who have subscribed to our newsletter',
      type: 'newsletter',
      contactCount: 5678,
      settings: {
        allowDuplicates: false,
        autoValidate: true,
        requireEmail: true,
        requirePhone: false
      }
    },
    {
      id: '3',
      name: 'Event Attendees',
      description: 'People who have attended our events',
      type: 'event',
      contactCount: 910,
      settings: {
        allowDuplicates: false,
        autoValidate: true,
        requireEmail: true,
        requirePhone: false
      }
    },
    {
      id: '4',
      name: 'Loyalty Program',
      description: 'Members of our loyalty program',
      type: 'loyalty',
      contactCount: 3456,
      settings: {
        allowDuplicates: false,
        autoValidate: true,
        requireEmail: true,
        requirePhone: true
      }
    }
  ]);

  const addGroup = (groupData: Omit<Group, 'id' | 'contactCount'>) => {
    const newGroup: Group = {
      ...groupData,
      id: Date.now().toString(),
      contactCount: 0
    };
    setGroups(prevGroups => [...prevGroups, newGroup]);
  };

  const updateGroup = (id: string, groupData: Partial<Group>) => {
    setGroups(prevGroups =>
      prevGroups.map(group =>
        group.id === id ? { ...group, ...groupData } : group
      )
    );
  };

  const deleteGroup = (id: string) => {
    setGroups(prevGroups => prevGroups.filter(group => group.id !== id));
  };

  return (
    <GroupContext.Provider value={{ groups, addGroup, updateGroup, deleteGroup }}>
      {children}
    </GroupContext.Provider>
  );
}; 